/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable consistent-return */
import { Flex, Pane, Popup, Text, Toast } from '@leverege/ui-elements'
import moment from 'moment'
import { useEffect, useState } from 'react'
import useTimeKeyInterval from '../../cycleTimeMonitoring/hooks/useTimeKeyInterval'
import useDebounce from '../hooks/useDebounce'
import ImagePreview from './ImagePreview'
import S from './ShellsTable.css'
import { formatPaceTimes, getBgColor, getStyle } from './Utils'

const stageNames = {
  'dry fit shear web assembly' : [ 'SW Dry-fit', 'Ajst Seco SW' ],
  'shear web bonding' : [ 'SW Bonding', 'Unión de SW' ],
  'shear web cure' : [ 'SW Cure', 'Curado de SW' ],
  'blade assembly prep' : [ 'Assembly Prep', 'Prep Ensamb' ],
  'assembly dry fit' : [ 'Assembly Dry-fit', 'Aj Seco Ensam' ],
  'paste application and blade assembly close' : [
    'Paste & Close',
    'Peg. & Cierre',
  ],
  'install inner laminate/paste cure' : [
    'Inner Laminates & Cure',
    'Lam Int & Curado',
  ],
  'de-mold' : [ 'Demold', 'Desmoldeo' ],
}

const getLastStage = stages => Object.values( stages ).findIndex( s => !!s?.startTime && !s?.endTime )
const partialDuration = ( stages ) => {
  const lastStageIndex = getLastStage( stages )
  const startTime = Object.values( stages )[lastStageIndex]?.startTime

  return formatPaceTimes( moment().diff( startTime, 's' ) / 3600 ) ?? '0.00'
}

function BondedTableCell( { value, index : i, dispatch, delegate, ...props } ) {
  const [ previewImages, setPreviewImages ] = useState( {} )
  const [ eventData, setEventData ] = useState()
  const debouncedEventData = useDebounce( eventData, 1000 )
  useTimeKeyInterval( 0.5 )

  const data = props?.row?.original
  const stages = data?.stages ?? {}
  const rowTitle = data?.rowTitle
  const columnTitle = Object.values( stages )?.[i]?.ctGroupDescription
  const isLast = i === getLastStage( stages )
  const isFinished = !props.row.original.inProgress
  const timings = data?.timings?.[i]
  const moldIds = data?.moldIds
  const shouldShowPopup = rowTitle === 'BND Actual' && !!timings?.[0]
  const targetPace = props?.data?.[1]?.paces?.[i]
  const hasColor = rowTitle === 'BND Actual' || rowTitle === 'Inspection'

  const loadEvent = async ( deviceId, start, end ) => dispatch( async ( _, __, { api } ) => {
    const url = delegate.getInterfaceUrl()

    return api.child( `v1/interface/${url}/moldHalves/${deviceId}.events` ).get( {
      q : JSON.stringify( {
        start,
        end,
        where : [ [ '$type', '=', 'occupancy' ] ]
      } )
    } ) 
  }
  )

  const handleImagesSetup = async ( { moldIds, timings } ) => {
    try {
      if ( !moldIds || !timings ) {
        return Toast.error( 'Event does not have enough data.', {
          autoClose : 2000,
        } )
      }

      let res
      let currMold

      const start = new Date( timings?.[0] ).valueOf()
      const finish = new Date( timings?.[1] ).valueOf()

      const psRes =
        moldIds?.[1] &&
        ( await Promise.all( [
          timings?.[0] && loadEvent( moldIds[1], start - 10, start + 10 ),
          timings?.[1] && loadEvent( moldIds[1], finish - 10, finish + 10 ),
        ] ) )

      if ( psRes?.[0]?.items?.length ) {
        res = psRes
        currMold = moldIds?.[1]
      } else {
        res =
          moldIds?.[0] &&
          ( await Promise.all( [
            timings?.[0] && loadEvent( moldIds[0], start - 10, start + 10 ),
            timings?.[1] && loadEvent( moldIds[0], finish - 10, finish + 10 ),
          ] ) )
        currMold = moldIds?.[0]
      }

      const first = res?.[0]?.items?.[0]?.data?.resources?.[0]?.path
      const last = res?.[1]?.items?.[0]?.data?.resources?.[0]?.path

      if ( ( !first && !last ) || !currMold ) return Toast.error( 'No images found.', { autoClose : 2000 } )

      setPreviewImages( { [currMold] : [ first, last ] } )
    } catch ( e ) {
      console.log( e )
    }
  }

  useEffect( () => {
    if ( !debouncedEventData?.timings ) return

    handleImagesSetup( debouncedEventData )
  }, [ debouncedEventData ] )

  if ( !shouldShowPopup ) {
    return (
      <Flex
        align='center'
        justify='center'
        className={S.row}
        style={{
          backgroundColor : getBgColor( {
            value : isLast ? partialDuration( stages ) : value,
            i,
            isLast,
            targetPace,
            isFinished,
            hasColor,
            rowTitle,
            timings,
            ...props,
          } ),
          ...getStyle( {
            value : isLast ? partialDuration( stages ) : value,
            i,
            isLast,
            targetPace,
            isFinished,
            hasColor,
            ...props,
          } ),
        }}>
        {isLast ? partialDuration( stages ) : value ?? '-'}
      </Flex>
    )
  }

  const trigger = ( { ref, setOpened } ) => (
    <div
      ref={ref}
      style={{
        height : '100%',
        width : '100%',
        cursor : shouldShowPopup ? 'pointer' : undefined,
        backgroundColor : getBgColor( {
          value : isLast ? partialDuration( stages ) : value,
          i,
          isLast,
          targetPace,
          isFinished,
          hasColor,
          ...props,
        } ),
        ...getStyle( {
          value : isLast ? partialDuration( stages ) : value,
          i,
          isLast,
          targetPace,
          isFinished,
          hasColor,
          ...props,
        } ),
      }}>
      <Flex
        align='center'
        justify='center'
        className={S.row}
        onm
        onClick={() => {
          setOpened( true )
          setEventData( { moldIds, timings } )
          setPreviewImages( s => ( {} ) )
        }}
        onMouseLeave={() => {
          // setOpened( false )
        }}>
        {isLast ? partialDuration( stages ) : value ?? '-'}
      </Flex>
    </div>
  )

  return (
    <Popup
      attachment='bottom right'
      targetAttachment='top right'
      trigger={trigger}
      style={{ color : '#394756', backgroundColor : 'transparent' }}>
      <Pane variant='factoryCycleTimeHover'>
        <Flex variant='colL'>
          <Text style={{ fontWeight : 700 }}>{rowTitle} - {stageNames?.[columnTitle][0]} ({stageNames?.[columnTitle][1]})</Text>
          <Flex
            gap={10}
            style={{ height : 200, minWidth : 355.6 }}
            align='center'
            justify='center'>
            {Object.entries( previewImages ).map( ( [ k, v ] ) => v.map( v => ( v ? (
              <ImagePreview
                src={v}
                itemId={k}
                delegate={delegate}
                dispatch={dispatch}/>
            ) : null ),
            ),
            )}
          </Flex>
          <Flex
            gap={10}
            style={{ minWidth : 200 }}
            align='center'
            justify='space-around'>
            {timings?.[0] && (
              <Flex gap={10} justify='space-between' align='center'>
                <Text>Start Time</Text>
                <Text style={{ fontWeight : 600 }}>
                  {moment( timings?.[0] ).format( 'LT M/D/YY' )}
                </Text>
              </Flex>
            )}
            {timings?.[1] && (
              <Flex gap={10} justify='space-between' align='center'>
                <Text>End Time</Text>
                <Text style={{ fontWeight : 600 }}>
                  {moment( timings?.[1] ).format( 'LT M/D/YY' )}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Pane>
    </Popup>
  )
}

export default BondedTableCell
